import { type RefObject, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

const scrollPositions = new Map<string, number>();

export const useScrollPositionRestoration = (
	scrollerRef: RefObject<HTMLElement | null>,
): void => {
	const { key } = useLocation();

	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!scroller) return;

		const savePosition = () => {
			scrollPositions.set(key, scroller.scrollTop);
		};

		scroller.addEventListener("scroll", savePosition, { passive: true });
		return () => {
			scroller.removeEventListener("scroll", savePosition);
		};
	}, [key, scrollerRef]);

	useLayoutEffect(() => {
		const scroller = scrollerRef.current;
		const savedPosition = scrollPositions.get(key);
		if (!scroller || savedPosition === undefined) return;

		const resizeObserver = new ResizeObserver(() => restorePosition());

		const stopRestoring = () => {
			resizeObserver.disconnect();
			scroller.removeEventListener("touchstart", stopRestoring);
			scroller.removeEventListener("wheel", stopRestoring);
		};

		const restorePosition = () => {
			scroller.scrollTop = savedPosition;
			if (scroller.scrollTop >= savedPosition) {
				stopRestoring();
			}
		};

		for (const child of Array.from(scroller.children)) {
			resizeObserver.observe(child);
		}
		scroller.addEventListener("touchstart", stopRestoring, { passive: true });
		scroller.addEventListener("wheel", stopRestoring, { passive: true });
		restorePosition();

		return stopRestoring;
	}, [key, scrollerRef]);
};
